import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { approvalAPI } from '../services/approvalApi';
import '../styles/AdminDashboard.css';
import '../styles/Approval.css';

const PRIORITIES = ['Low', 'Normal', 'High', 'Urgent'];
const STATUSES = ['Pending', 'Waiting', 'Returned', 'Approved', 'Rejected'];
const DEPARTMENTS = [
  'Academics', 'Finance', 'Health Services', 'Kitchen & Catering', 'Administration',
  'Operations', 'Security', 'Transport', 'IT & Communications', 'Library',
  'Sports & Extracurricular', 'Facilities', 'General'
];

const ROLE_LABELS = { requester: 'Requester', approver: 'Approver', finance: 'Finance', admin: 'Administrator' };

const formatDate = (d) => {
  if (!d) return '—';
  const date = new Date(d);
  if (isNaN(date.getTime())) return String(d);
  return date.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
  });
};

const formatAmount = (a, c) => {
  const n = Number(a || 0);
  return n > 0 ? n.toLocaleString('en-US') + ' ' + (c || 'RWF') : '—';
};

const statusClass = (s) => {
  const map = { Pending: 'pending', Waiting: 'waiting', Returned: 'returned', Approved: 'approved', Rejected: 'rejected' };
  return map[s] || '';
};

const ApprovalDashboard = ({ user, onLogout }) => {
  const { id: routeRequestId } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState('requests');
  const [categories, setCategories] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [detail, setDetail] = useState(null);
  const [actionModal, setActionModal] = useState(null);
  const [submittingAction, setSubmittingAction] = useState(false);
  const [msg, setMsg] = useState('');
  const [msgError, setMsgError] = useState(false);

  // New request form
  const [form, setForm] = useState({
    categoryId: '', title: '', description: '', department: 'General',
    priority: 'Normal', amount: '', currency: 'RWF', files: []
  });
  const [submitting, setSubmitting] = useState(false);

  // Users (admin)
  const [users, setUsers] = useState([]);
  const [newUser, setNewUser] = useState({ email: '', name: '', department: 'General', role: 'requester', position: '' });
  const [editUser, setEditUser] = useState(null);
  const [userMsg, setUserMsg] = useState('');

  // Categories (admin)
  const [allCategories, setAllCategories] = useState([]);
  const [catModal, setCatModal] = useState(null);

  // Positions (admin)
  const [positions, setPositions] = useState([]);
  const [posForm, setPosForm] = useState({ name: '', description: '' });
  const [posMsg, setPosMsg] = useState('');
  const [editPos, setEditPos] = useState(null);

  // Departments (admin-defined; also used in request/user forms)
  const [departments, setDepartments] = useState([]);
  const [deptForm, setDeptForm] = useState({ name: '', description: '' });
  const [deptMsg, setDeptMsg] = useState('');
  const [editDept, setEditDept] = useState(null);

  // Notifications
  const [notifs, setNotifs] = useState({ notifications: [], unread: 0 });
  const [notifOpen, setNotifOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  // Stats (admin)
  const [stats, setStats] = useState(null);

  const isAdmin = user.role === 'admin';
  const isApprover = user.role === 'approver';
  const isFinance = user.role === 'finance';

  const avatarInitial = ((user.name || user.email || '?').trim().charAt(0) || '?').toUpperCase();

  const deptOptions = departments.length ? departments.map((d) => d.name) : DEPARTMENTS;

  const tabs = [];
  if (isAdmin) {
    tabs.push(
      { key: 'requests', label: 'All Requests' },
      { key: 'new', label: 'New Request' },
      { key: 'users', label: 'Users' },
      { key: 'categories', label: 'Categories' },
      { key: 'positions', label: 'Positions' },
      { key: 'departments', label: 'Departments' }
    );
  } else if (isApprover) {
    tabs.push({ key: 'requests', label: 'Requests' }, { key: 'inbox', label: 'For Approval' }, { key: 'new', label: 'New Request' });
  } else {
    tabs.push({ key: 'requests', label: isFinance ? 'Approved Documents' : 'My Requests' }, { key: 'new', label: 'New Request' });
  }

  const showMsg = (text, isError) => {
    setMsg(text);
    setMsgError(!!isError);
    setTimeout(() => { setMsg(''); setMsgError(false); }, 6000);
  };

  const loadRequests = useCallback((f = 'all', silent = false) => {
    if (!silent) setLoading(true);
    approvalAPI.getRequests(f && f !== 'all' ? { filter: f } : {})
      .then((res) => setRequests(res.data))
      .catch(() => setRequests([]))
      .finally(() => { if (!silent) setLoading(false); });
  }, []);

  const loadCategories = useCallback(() => {
    approvalAPI.getCategories().then((res) => setCategories(res.data)).catch(() => {});
  }, []);

  const loadNotifications = useCallback(() => {
    approvalAPI.getNotifications().then((res) => setNotifs(res.data)).catch(() => {});
  }, []);

  const loadPositions = useCallback(() => {
    approvalAPI.getPositions().then((res) => setPositions(res.data)).catch(() => {});
  }, []);

  const loadDepartments = useCallback(() => {
    approvalAPI.getDepartments().then((res) => setDepartments(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    loadRequests();
    loadCategories();
    loadNotifications();
    loadDepartments();
    if (isAdmin) {
      approvalAPI.getUsers().then((res) => setUsers(res.data)).catch(() => {});
      approvalAPI.getAllCategories().then((res) => setAllCategories(res.data)).catch(() => {});
      approvalAPI.getStats().then((res) => setStats(res.data)).catch(() => {});
      loadPositions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadRequests, loadCategories, loadNotifications, loadDepartments, isAdmin]);

  // Live updates: silently poll so approvals/status changes appear without a manual refresh.
  useEffect(() => {
    const refresh = () => {
      loadRequests(filter, true);
      loadNotifications();
      if (isAdmin) approvalAPI.getStats().then((res) => setStats(res.data)).catch(() => {});
    };
    const t = setInterval(refresh, 15000);
    return () => clearInterval(t);
  }, [loadRequests, loadNotifications, isAdmin, filter]);

  useEffect(() => {
    if (routeRequestId) openDetail(routeRequestId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeRequestId]);

  const openDetail = (id) => {
    approvalAPI.getRequest(id)
      .then((res) => setDetail(res.data))
      .catch((err) => showMsg(err.response?.data?.message || 'Failed to load request', true));
  };

  const closeDetail = () => {
    closeDetail();
    if (routeRequestId) navigate('/approval/dashboard');
  };

  // ============ NEW REQUEST ============
  const submitRequest = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const fd = new FormData();
    fd.append('data', JSON.stringify({
      categoryId: form.categoryId, title: form.title, description: form.description,
      department: form.department, priority: form.priority, amount: form.amount || 0, currency: form.currency
    }));
    [...form.files].forEach((f) => fd.append('attachments', f));
    try {
      const res = await approvalAPI.createRequest(fd);
      showMsg(res.data.message);
      setForm({ categoryId: '', title: '', description: '', department: 'General', priority: 'Normal', amount: '', currency: 'RWF', files: [] });
      loadRequests();
      setTab('requests');
    } catch (err) {
      showMsg(err.response?.data?.message || 'Failed to submit request', true);
    } finally {
      setSubmitting(false);
    }
  };

  // ============ ACTION ============
  const performAction = async (action, reason) => {
    if (submittingAction) return;
    setSubmittingAction(true);
    try {
      const res = await approvalAPI.takeAction(detail.request.id, { action, reason });
      showMsg(res.data.message);
      setActionModal(null);
      closeDetail();
      loadRequests();
      loadNotifications();
      if (isAdmin) approvalAPI.getStats().then((r) => setStats(r.data)).catch(() => {});
    } catch (err) {
      setActionModal(null);
      const msg = err.response?.data?.message || 'Action failed';
      if (err.response?.status === 400 && /finalized|already/i.test(msg)) {
        showMsg('This request was already processed. Refreshing the list…');
        closeDetail();
        loadRequests();
        loadNotifications();
      } else {
        showMsg(msg, true);
      }
    } finally {
      setSubmittingAction(false);
    }
  };

  // ============ RESUBMIT ============
  const [resubmitForm, setResubmitForm] = useState(null);
  const submitResubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const fd = new FormData();
    fd.append('data', JSON.stringify({
      title: resubmitForm.title, description: resubmitForm.description,
      department: resubmitForm.department, priority: resubmitForm.priority,
      amount: resubmitForm.amount || 0, currency: resubmitForm.currency
    }));
    [...resubmitForm.files].forEach((f) => fd.append('attachments', f));
    try {
      const res = await approvalAPI.resubmit(detail.request.id, fd);
      showMsg(res.data.message);
      setResubmitForm(null);
      closeDetail();
      loadRequests();
      loadNotifications();
    } catch (err) {
      showMsg(err.response?.data?.message || 'Resubmit failed', true);
    } finally {
      setSubmitting(false);
    }
  };

  const cancelRequest = async () => {
    try {
      const res = await approvalAPI.cancel(detail.request.id);
      showMsg(res.data.message);
      closeDetail();
      loadRequests();
    } catch (err) {
      showMsg(err.response?.data?.message || 'Cancel failed', true);
    }
  };

  const downloadDoc = () => {
    window.open(approvalAPI.documentUrl(detail.request.id), '_blank');
  };

  const markNotifsRead = () => {
    approvalAPI.markNotificationsRead().then(() => loadNotifications());
  };

  // ============ USERS (admin) ============
  const addUser = async (e) => {
    e.preventDefault();
    try {
      await approvalAPI.createUser(newUser);
      setUserMsg('User added successfully');
      setNewUser({ email: '', name: '', department: 'General', role: 'requester', position: '' });
      approvalAPI.getUsers().then((res) => setUsers(res.data));
    } catch (err) {
      setUserMsg(err.response?.data?.message || 'Failed to add user');
    }
  };

  const saveEditUser = async (e) => {
    e.preventDefault();
    if (!editUser) return;
    try {
      await approvalAPI.updateUser(editUser.id, editUser);
      setEditUser(null);
      setUserMsg('User updated successfully');
      approvalAPI.getUsers().then((res) => setUsers(res.data));
    } catch (err) {
      setUserMsg(err.response?.data?.message || 'Failed to update user');
    }
  };

  const toggleUser = async (u) => {
    const action = u.isActive ? 'deactivateUser' : 'activateUser';
    try {
      await approvalAPI[action](u.id);
    } catch (err) {
      setUserMsg(err.response?.data?.message || 'Action failed');
    }
    approvalAPI.getUsers().then((res) => setUsers(res.data));
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Delete ${u.email} permanently?`)) return;
    await approvalAPI.deleteUser(u.id);
    approvalAPI.getUsers().then((res) => setUsers(res.data));
  };

  // ============ CATEGORIES (admin) ============
  const openCatModal = (mode, category) => {
    if (mode === 'create') {
      setCatModal({ mode, id: null, name: '', description: '', workflow: [] });
    } else if (category) {
      setCatModal({
        mode, id: category.id, name: category.name, description: category.description,
        workflow: (category.workflow || []).map((w) => ({ step_order: w.step_order, role_title: w.role_title }))
      });
    }
  };

  const catModalMsgRef = React.useRef('');
  const [catModalMsg, setCatModalMsg] = React.useState('');
  catModalMsgRef.current = catModalMsg;

  const saveCategory = async () => {
    if (!catModal.name.trim()) { setCatModalMsg('Category name is required'); return; }
    const steps = catModal.workflow
      .map((w, i) => ({ step_order: i + 1, role_title: (w.role_title || '').trim() }))
      .filter((w) => w.role_title);
    if (steps.length === 0) { setCatModalMsg('Add at least one approval step'); return; }
    try {
      const payload = { name: catModal.name.trim(), description: catModal.description, workflow: steps };
      if (catModal.mode === 'create') {
        await approvalAPI.createCategory(payload);
      } else {
        await approvalAPI.updateCategory(catModal.id, payload);
      }
      setCatModal(null);
      setCatModalMsg('');
      approvalAPI.getAllCategories().then((res) => setAllCategories(res.data));
    } catch (err) {
      setCatModalMsg(err.response?.data?.message || 'Failed to save category');
    }
  };

  const toggleCategory = async (c) => {
    await approvalAPI.toggleCategory(c.id);
    approvalAPI.getAllCategories().then((res) => setAllCategories(res.data));
  };

  const deleteCategory = async (c) => {
    if (!window.confirm(`Delete category "${c.name}" permanently?`)) return;
    await approvalAPI.deleteCategory(c.id);
    approvalAPI.getAllCategories().then((res) => setAllCategories(res.data));
  };

  // ============ POSITIONS (admin) ============
  const addPosition = async (e) => {
    e.preventDefault();
    if (!posForm.name.trim()) { setPosMsg('Position name is required'); return; }
    try {
      await approvalAPI.createPosition({ name: posForm.name, description: posForm.description });
      setPosMsg('Position added successfully');
      setPosForm({ name: '', description: '' });
      loadPositions();
    } catch (err) {
      setPosMsg(err.response?.data?.message || 'Failed to add position');
    }
  };

  const togglePosition = async (p) => {
    await approvalAPI.updatePosition(p.id, { isActive: p.isActive ? 0 : 1 });
    loadPositions();
  };

  const deletePosition = async (p) => {
    if (!window.confirm(`Delete position "${p.name}"?`)) return;
    try {
      await approvalAPI.deletePosition(p.id);
      setPosMsg('Position deleted');
      loadPositions();
    } catch (err) {
      setPosMsg(err.response?.data?.message || 'Failed to delete position');
    }
  };

  const savePositionEdit = async () => {
    if (!editPos) return;
    if (!editPos.name.trim()) { setPosMsg('Position name is required'); return; }
    try {
      await approvalAPI.updatePosition(editPos.id, { name: editPos.name, description: editPos.description });
      setEditPos(null);
      setPosMsg('Position updated successfully');
      loadPositions();
    } catch (err) {
      setPosMsg(err.response?.data?.message || 'Failed to update position');
    }
  };

  // ============ DEPARTMENTS (admin) ============
  const addDepartment = async (e) => {
    e.preventDefault();
    if (!deptForm.name.trim()) { setDeptMsg('Department name is required'); return; }
    try {
      await approvalAPI.createDepartment({ name: deptForm.name, description: deptForm.description });
      setDeptMsg('Department added successfully');
      setDeptForm({ name: '', description: '' });
      loadDepartments();
    } catch (err) {
      setDeptMsg(err.response?.data?.message || 'Failed to add department');
    }
  };

  const saveDepartmentEdit = async () => {
    if (!editDept) return;
    if (!editDept.name.trim()) { setDeptMsg('Department name is required'); return; }
    try {
      await approvalAPI.updateDepartment(editDept.id, { name: editDept.name, description: editDept.description });
      setEditDept(null);
      setDeptMsg('Department updated successfully');
      loadDepartments();
    } catch (err) {
      setDeptMsg(err.response?.data?.message || 'Failed to update department');
    }
  };

  const toggleDepartment = async (d) => {
    await approvalAPI.updateDepartment(d.id, { isActive: d.isActive ? 0 : 1 });
    loadDepartments();
  };

  const deleteDepartment = async (d) => {
    if (!window.confirm(`Delete department "${d.name}"?`)) return;
    try {
      await approvalAPI.deleteDepartment(d.id);
      setDeptMsg('Department deleted');
      loadDepartments();
    } catch (err) {
      setDeptMsg(err.response?.data?.message || 'Failed to delete department');
    }
  };

  // ============ RENDER ============

  const renderStepTimeline = (steps) => (
    <div className="approval-steps">
      {steps.map((s) => (
        <div key={s.id} className={`approval-step ${statusClass(s.status)}`}>
          <div className="approval-step-dot"></div>
          <div className="approval-step-body">
            <div className="approval-step-title">{s.role_title}</div>
            <div className="approval-step-meta">
              {s.approver_email || 'Unassigned'} · <span className={`approval-step-status ${statusClass(s.status)}`}>{s.status}</span>
            </div>
            {s.decided_by && (
              <div className="approval-step-reason">
                {s.decision_reason && <p><strong>Reason:</strong> {s.decision_reason}</p>}
                <p><strong>Decided by:</strong> {s.decided_by} · {formatDate(s.decided_at)}</p>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  const renderActions = () => {
    const r = detail.request;
    if (r.status === 'Approved' || r.status === 'Rejected') {
      return (
        <div className="approval-actions">
          {['Approved', 'Rejected'].includes(r.status) && r.approvedPdfPath && (
            <button className="btn-view" onClick={downloadDoc}>Download Signed Document</button>
          )}
          {r.status === 'Rejected' && !r.approvedPdfPath && <span className="approval-note">This request was not approved.</span>}
          {isAdmin && ['Approved', 'Rejected'].includes(r.status) && r.approvedPdfPath && <button className="btn-approve" onClick={downloadDoc}>Open PDF</button>}
        </div>
      );
    }
    // Strict workflow: only the approver assigned to the current step may act.
    // Admins can view every request but cannot act unless they are the current
    // step's assigned approver; no single admin can finalize a multi-step flow.
    const currentStep = detail.steps.find((s) => s.step_order === r.current_step);
    const isCurrentApprover = !!currentStep && (
      currentStep.approver_email === user.email ||
      (currentStep.role_title && currentStep.role_title === user.approver_title)
    );
    if (isCurrentApprover && ['Pending', 'Waiting'].includes(r.status)) {
      return (
        <div className="approval-actions">
          <button className="btn-approve" onClick={() => setActionModal({ action: 'approve', label: 'Approve' })}>Approve</button>
          <button className="btn-reject" onClick={() => setActionModal({ action: 'reject', label: 'Reject' })}>Reject</button>
          <button className="btn-wait" onClick={() => setActionModal({ action: 'return', label: 'Return for More Info' })}>Return</button>
          <button className="btn-wait" onClick={() => setActionModal({ action: 'wait', label: 'Mark Waiting' })}>Wait</button>
        </div>
      );
    }
    if (r.requester_email === user.email && ['Pending', 'Waiting', 'Returned'].includes(r.status)) {
      return (
        <div className="approval-actions">
          {r.status === 'Returned' && (
            <button className="btn-approve" onClick={() => setResubmitForm({
              title: r.title, description: r.description, department: r.department,
              priority: r.priority, amount: r.amount, currency: r.currency, files: []
            })}>Edit &amp; Resubmit</button>
          )}
          <button className="btn-reject" onClick={cancelRequest}>Cancel Request</button>
        </div>
      );
    }
    if (['Approved', 'Rejected'].includes(r.status) && r.approvedPdfPath) {
      return (
        <div className="approval-actions">
          <button className="btn-view" onClick={downloadDoc}>Download Signed Document</button>
        </div>
      );
    }
    return null;
  };

  const renderRequestsList = (rows) => (
    <div className="applications-table">
      <h2>Requests</h2>
      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>Number</th>
              <th>Title</th>
              <th>Category</th>
              <th>Requester</th>
              <th>Priority</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Date</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan="9" className="no-data">No requests found</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className={r.pending_for_me ? 'approval-action-needed' : ''}>
                <td><strong>{r.requestNumber}</strong></td>
                <td>
                  {r.title}
                  {!!r.pending_for_me && <span className="approval-need-badge">Action needed</span>}
                </td>
                <td>{r.category_name}</td>
                <td>{r.requester_name || r.requester_email}</td>
                <td><span className={`approval-priority ${r.priority?.toLowerCase()}`}>{r.priority}</span></td>
                <td>{formatAmount(r.amount, r.currency)}</td>
                <td><span className={`status-badge ${statusClass(r.status)}`}>{r.status}</span></td>
                <td>{formatDate(r.created_at)}</td>
                <td><button className="btn-view" onClick={() => openDetail(r.id)}>View</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderForm = (f, setF, submit, isResubmit) => (
    <form className="approval-form" onSubmit={submit}>
      {!isResubmit && (
        <div className="approval-form-row">
          <label>Category</label>
          <select value={f.categoryId} onChange={(e) => setF({ ...f, categoryId: e.target.value })} required>
            <option value="">Select category</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
      )}
      <div className="approval-form-row">
        <label>Title</label>
        <input type="text" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} required placeholder="Short summary of the request" />
      </div>
      <div className="approval-form-row">
        <label>Description</label>
        <textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} required placeholder="Provide full details..." rows="5" />
      </div>
      <div className="approval-form-grid">
        <div className="approval-form-row">
          <label>Department</label>
          <select value={f.department} onChange={(e) => setF({ ...f, department: e.target.value })}>
            {deptOptions.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <div className="approval-form-row">
          <label>Priority</label>
          <select value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value })}>
            {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div className="approval-form-row">
          <label>Amount (optional)</label>
          <input type="number" step="0.01" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} placeholder="e.g. 250000" />
        </div>
        <div className="approval-form-row">
          <label>Currency</label>
          <select value={f.currency} onChange={(e) => setF({ ...f, currency: e.target.value })}>
            <option value="RWF">RWF</option>
            <option value="USD">USD</option>
            <option value="EUR">EUR</option>
          </select>
        </div>
      </div>
      <div className="approval-form-row">
        <label>Attachments (optional)</label>
        <input type="file" multiple onChange={(e) => setF({ ...f, files: e.target.files })} />
      </div>
      <button className="btn-approve approval-submit" type="submit" disabled={submitting}>
        {submitting ? 'Submitting...' : (isResubmit ? 'Resubmit Request' : 'Submit Request')}
      </button>
    </form>
  );

  return (
    <div className="admin-dashboard approval-dashboard">
      {/* ============ HEADER ============ */}
      <div className="admin-header approval-header">
        <img src="/school.png" alt="Hope Haven" className="admin-logo" />
        <div>
          <h1>Approval &amp; Sign-off System</h1>
          <p className="admin-subtitle">Hope Haven Christian School</p>
        </div>
        <div className="admin-user-info approval-user-info">
          {userMenuOpen && <div className="approval-menu-backdrop" onClick={() => setUserMenuOpen(false)}></div>}
          <div className="approval-notif-wrap">
            <button className="approval-notif-btn" onClick={() => { setNotifOpen(!notifOpen); if (!notifOpen) loadNotifications(); }} aria-label="Notifications">
              <span className="approval-bell">🔔</span>
              {notifs.unread > 0 && <span className="approval-notif-count">{notifs.unread}</span>}
            </button>
            {notifOpen && (
              <div className="approval-notif-dropdown">
                <div className="approval-notif-head">
                  <strong>Notifications</strong>
                  <button onClick={markNotifsRead}>Mark all read</button>
                </div>
                {notifs.notifications.length === 0 && <p className="no-data">No notifications</p>}
                {notifs.notifications.map((n) => (
                  <div key={n.id} className={`approval-notif-item ${n.is_read ? '' : 'unread'}`}>
                    <strong>{n.title}</strong>
                    <p>{n.message}</p>
                    <small>{formatDate(n.created_at)}</small>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="approval-user-menu-wrap">
            <button className="approval-user-chip" onClick={() => setUserMenuOpen(!userMenuOpen)} aria-label="Account menu">
              <span className="approval-avatar">{avatarInitial}</span>
              <span className="approval-user-name">{user.name || user.email}</span>
              <span className="approval-caret">▾</span>
            </button>
            {userMenuOpen && (
              <div className="approval-user-dropdown">
                <div className="approval-ud-head">
                  <span className="approval-avatar">{avatarInitial}</span>
                  <div className="approval-ud-head-text">
                    <strong>{user.name || user.email}</strong>
                    <span>{user.email}</span>
                  </div>
                </div>
                <div className="approval-ud-row"><span>Role</span><strong>{ROLE_LABELS[user.role] || user.role}</strong></div>
                {user.approver_title && <div className="approval-ud-row"><span>Position</span><strong>{user.approver_title}</strong></div>}
                <div className="approval-ud-row"><span>Department</span><strong>{user.department || 'General'}</strong></div>
                <div className="approval-ud-divider"></div>
                <button className="approval-ud-item" onClick={() => { setUserMenuOpen(false); setHelpOpen(true); }}>Help &amp; Guide</button>
                <button className="approval-ud-item danger" onClick={onLogout}>Sign Out</button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="admin-content">
        {msg && <div className={`approval-msg ${msgError ? 'error' : ''}`}>{msg}</div>}

        {/* ============ STATS (admin) ============ */}
        {isAdmin && stats && (
          <div className="stats-grid">
            <div className="stat-card pending"><h3>Pending</h3><div className="stat-number">{stats.stats.pending}</div></div>
            <div className="stat-card accepted"><h3>Approved</h3><div className="stat-number">{stats.stats.approved}</div></div>
            <div className="stat-card rejected"><h3>Rejected</h3><div className="stat-number">{stats.stats.rejected}</div></div>
            <div className="stat-card pending"><h3>Returned</h3><div className="stat-number">{stats.stats.returned}</div></div>
            <div className="stat-card pending"><h3>Total</h3><div className="stat-number">{stats.stats.total}</div></div>
          </div>
        )}

        {/* ============ TABS ============ */}
        <div className="dashboard-tabs">
          {tabs.map((t) => (
            <button
              key={t.key}
              className={`dashboard-tab ${tab === t.key ? 'active' : ''}`}
              onClick={() => setTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* ============ NEW REQUEST ============ */}
        {tab === 'new' && (
          <div className="admin-management">
            <h2>New Request</h2>
            <p className="approval-intro">
              Submit a request. It will be routed through the approval flow configured for the selected category.
            </p>
            {renderForm(form, setForm, submitRequest, false)}
          </div>
        )}

        {/* ============ REQUESTS LIST ============ */}
        {tab === 'requests' && (
          <>
            <div className="controls">
              <div className="filter-group">
                <label>Status:</label>
                <select value={filter} onChange={(e) => { setFilter(e.target.value); loadRequests(e.target.value); }}>
                  <option value="all">All</option>
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            {loading ? <div className="loading">Loading requests...</div> : renderRequestsList(requests)}
          </>
        )}

        {/* ============ APPROVER INBOX ============ */}
        {tab === 'inbox' && (
          <>
            {loading ? <div className="loading">Loading...</div> : renderRequestsList(requests.filter((r) => !!r.pending_for_me))}
          </>
        )}

        {/* ============ USERS (admin) ============ */}
        {tab === 'users' && isAdmin && (
          <div className="admin-management">
            <h2>Approval Users</h2>
            <p className="approval-intro">
              Only an <strong>Administrator</strong> can be added with admin rights. Everyone else signs in as a
              <strong> Requester</strong>. Assign a <strong>Position</strong> (e.g. Director of IT, Agriculture Manager)
              to make that user the approver for the position in the workflow. Finance users can view approved documents.
            </p>
            {userMsg && <div className="approval-msg">{userMsg}</div>}
            <form className="approval-add-user-grid" onSubmit={addUser}>
              <input type="email" placeholder="email@hopehavenschool.org" value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} required />
              <input type="text" placeholder="Full name" value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} />
              <select value={newUser.department} onChange={(e) => setNewUser({ ...newUser, department: e.target.value })}>
                {deptOptions.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
              <select value={newUser.role} onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}>
                <option value="requester">Requester</option>
                <option value="finance">Finance</option>
                <option value="admin">Administrator</option>
              </select>
              <select value={newUser.position} onChange={(e) => setNewUser({ ...newUser, position: e.target.value })}>
                <option value="">No position (Requester)</option>
                {positions.filter((p) => p.isActive).map((p) => (
                  <option key={p.id} value={p.name}>{p.name}</option>
                ))}
              </select>
              <button type="submit">Add User</button>
            </form>
            <div className="admin-users-list">
              {users.map((u) => (
                <div key={u.id} className={`admin-user-row ${u.isActive ? '' : 'inactive'}`}>
                  <span>{u.name || u.email}</span>
                  <span>{u.email}</span>
                  <span>{u.department || 'General'}</span>
                  <span>
                    <span className="approval-role-badge">{ROLE_LABELS[u.role] || u.role}</span>
                    {u.approver_title && <span className="super-badge">{u.approver_title}</span>}
                    {!u.isActive && <span className="status-badge rejected">Inactive</span>}
                  </span>
                  <button onClick={() => setEditUser({ id: u.id, email: u.email, name: u.name, department: u.department, role: u.role, position: u.approver_title || '' })}>Edit</button>
                  <button onClick={() => toggleUser(u)}>{u.isActive ? 'Deactivate' : 'Activate'}</button>
                  <button className="danger" onClick={() => deleteUser(u)}>Delete</button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ============ CATEGORIES (admin) ============ */}
        {tab === 'categories' && isAdmin && (
          <div className="admin-management">
            <h2>Request Categories &amp; Approval Flows</h2>
            <p className="approval-intro">
              Set the flow plan for each category: define the positions that must sign off on a request and the order
              (e.g. Agriculture Manager → IT Director → Facility Manager → Managing Director → Finance).
            </p>
            <div className="approval-toolbar">
              <button className="btn-approve" onClick={() => openCatModal('create', null)}>+ Add Category</button>
            </div>
            <div className="admin-users-list">
              {allCategories.map((c) => (
                <div key={c.id} className={`admin-user-row ${c.isActive ? '' : 'inactive'}`}>
                  <span><strong>{c.name}</strong></span>
                  <span>{(c.workflow || []).length ? (c.workflow || []).map((w) => w.role_title).join(' → ') : <em>No workflow</em>}</span>
                  <span>{c.description}</span>
                  <button onClick={() => openCatModal('edit', c)}>Edit</button>
                  <button onClick={() => toggleCategory(c)}>{c.isActive ? 'Disable' : 'Enable'}</button>
                  <button className="danger" onClick={() => deleteCategory(c)}>Delete</button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ============ POSITIONS (admin) ============ */}
        {tab === 'positions' && isAdmin && (
          <div className="admin-management">
            <h2>Approval Positions</h2>
            <p className="approval-intro">
              Positions are the sign-off roles used in workflows (e.g. Director of IT, Agriculture Manager,
              Managing Director). Assign a position to a user in the <strong>Users</strong> tab so the system can
              route requests to them.
            </p>
            {posMsg && <div className="approval-msg">{posMsg}</div>}
            <form className="approval-add-user-grid" onSubmit={addPosition}>
              <input type="text" placeholder="Position name (e.g. Director of IT)" value={posForm.name}
                onChange={(e) => setPosForm({ ...posForm, name: e.target.value })} required />
              <input type="text" placeholder="Short description (optional)" value={posForm.description}
                onChange={(e) => setPosForm({ ...posForm, description: e.target.value })} />
              <button type="submit">Add Position</button>
            </form>
            <div className="admin-users-list">
              {positions.length === 0 && <p className="no-data">No positions yet. Add the sign-off roles used by the school.</p>}
              {positions.map((p) => (
                <div key={p.id} className={`admin-user-row ${p.isActive ? '' : 'inactive'}`}>
                  <span><strong>{p.name}</strong></span>
                  <span>{p.description}</span>
                  <span>
                    {p.isActive
                      ? <span className="status-badge approved">Active</span>
                      : <span className="status-badge rejected">Inactive</span>}
                  </span>
                  <button onClick={() => setEditPos({ id: p.id, name: p.name, description: p.description || '' })}>Edit</button>
                  <button onClick={() => togglePosition(p)}>{p.isActive ? 'Disable' : 'Enable'}</button>
                  <button className="danger" onClick={() => deletePosition(p)}>Delete</button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ============ DEPARTMENTS (admin) ============ */}
        {tab === 'departments' && isAdmin && (
          <div className="admin-management">
            <h2>Approval Departments</h2>
            <p className="approval-intro">
              Departments are the school sections users and requests belong to (e.g. Finance, Operations, IT &amp; Communications).
              Users pick a department when their account is created or edited. A department that is still in use cannot be deleted.
            </p>
            {deptMsg && <div className="approval-msg">{deptMsg}</div>}
            <form className="approval-add-user-grid" onSubmit={addDepartment}>
              <input type="text" placeholder="Department name (e.g. Administration)" value={deptForm.name}
                onChange={(e) => setDeptForm({ ...deptForm, name: e.target.value })} required />
              <input type="text" placeholder="Short description (optional)" value={deptForm.description}
                onChange={(e) => setDeptForm({ ...deptForm, description: e.target.value })} />
              <button type="submit">Add Department</button>
            </form>
            <div className="admin-users-list">
              {departments.length === 0 && <p className="no-data">No departments yet. Add the school sections users belong to.</p>}
              {departments.map((d) => (
                <div key={d.id} className={`admin-user-row ${d.isActive ? '' : 'inactive'}`}>
                  <span><strong>{d.name}</strong></span>
                  <span>{d.description}</span>
                  <span>
                    {d.isActive
                      ? <span className="status-badge approved">Active</span>
                      : <span className="status-badge rejected">Inactive</span>}
                  </span>
                  <button onClick={() => setEditDept({ id: d.id, name: d.name, description: d.description || '' })}>Edit</button>
                  <button onClick={() => toggleDepartment(d)}>{d.isActive ? 'Disable' : 'Enable'}</button>
                  <button className="danger" onClick={() => deleteDepartment(d)}>Delete</button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ============ DETAIL MODAL ============ */}
      {detail && (
        <div className="modal-overlay" onClick={() => setDetail(null)}>
          <div className="modal-content approval-detail" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setDetail(null)}>×</button>
            <h2>{detail.request.requestNumber} · {detail.request.title}</h2>

            <div className="modal-body">
              <div className="approval-detail-status">
                <span className={`status-badge ${statusClass(detail.request.status)}`}>{detail.request.status}</span>
                <span className={`approval-priority ${detail.request.priority?.toLowerCase()}`}>{detail.request.priority} priority</span>
              </div>

              <div className="detail-section">
                <h3>Request Details</h3>
                <p><strong>Category:</strong> {detail.request.category_name}</p>
                <p><strong>Requested by:</strong> {detail.request.requester_name || detail.request.requester_email}</p>
                <p><strong>Department:</strong> {detail.request.department}</p>
                <p><strong>Amount:</strong> {formatAmount(detail.request.amount, detail.request.currency)}</p>
                <p><strong>Submitted:</strong> {formatDate(detail.request.created_at)}</p>
                <p><strong>Description:</strong> {detail.request.description}</p>
              </div>

              {detail.attachments.length > 0 && (
                <div className="detail-section">
                  <h3>Attachments ({detail.attachments.length})</h3>
                  {detail.attachments.map((a) => (
                    <p key={a.id}>
                      <a className="file-link" href={`/uploads/${a.file_path}`} target="_blank" rel="noreferrer">
                        {a.original_name}
                      </a>
                    </p>
                  ))}
                </div>
              )}

              <div className="detail-section">
                <h3>Approval Workflow</h3>
                {renderStepTimeline(detail.steps)}
              </div>

              {renderActions()}

              {resubmitForm && (
                <div className="detail-section">
                  <h3>Edit &amp; Resubmit</h3>
                  {renderForm(resubmitForm, setResubmitForm, submitResubmit, true)}
                </div>
              )}

              {detail.history.length > 0 && (
                <div className="detail-section">
                  <h3>Activity History</h3>
                  {detail.history.map((h) => (
                    <p key={h.id}>
                      <strong>{h.action}</strong> — {h.actor_name || h.actor_email || 'System'}
                      <span className="approval-history-date"> {formatDate(h.created_at)}</span>
                      {h.reason && <em> ({h.reason})</em>}
                    </p>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ============ ACTION MODAL ============ */}
      {actionModal && (
        <ActionModal
          request={detail?.request}
          modal={actionModal}
          submitting={submittingAction}
          onClose={() => setActionModal(null)}
          onConfirm={performAction}
        />
      )}

      {/* ============ CATEGORY MODAL (admin) ============ */}
      {catModal && (
        <CategoryModal
          modal={catModal}
          setModal={setCatModal}
          positions={positions}
          msg={catModalMsg}
          onClose={() => { setCatModal(null); setCatModalMsg(''); }}
          onSave={saveCategory}
        />
      )}

      {/* ============ EDIT USER MODAL (admin) ============ */}
      {editUser && (
        <EditUserModal
          user={editUser}
          setUser={setEditUser}
          positions={positions}
          deptOptions={deptOptions}
          onClose={() => setEditUser(null)}
          onSave={saveEditUser}
        />
      )}

      {/* ============ EDIT POSITION MODAL (admin) ============ */}
      {editPos && (
        <EditNamedModal
          title="Edit Position"
          item={editPos}
          setItem={setEditPos}
          namePlaceholder="Position name (e.g. Director of IT)"
          onClose={() => setEditPos(null)}
          onSave={savePositionEdit}
        />
      )}

      {/* ============ EDIT DEPARTMENT MODAL (admin) ============ */}
      {editDept && (
        <EditNamedModal
          title="Edit Department"
          item={editDept}
          setItem={setEditDept}
          namePlaceholder="Department name (e.g. Administration)"
          onClose={() => setEditDept(null)}
          onSave={saveDepartmentEdit}
        />
      )}

      {/* ============ HELP MODAL ============ */}
      {helpOpen && (
        <div className="modal-overlay" onClick={() => setHelpOpen(false)}>
          <div className="modal-content" style={{ maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setHelpOpen(false)}>×</button>
            <h2>Help &amp; Guide</h2>
            <div className="modal-body approval-help-body">
              <div className="approval-help-section">
                <h3>Submitting a request</h3>
                <p>Open <strong>New Request</strong>, choose a category, fill in the details and submit. The request is routed to the positions in the category's approval flow. You will receive the signed document once all approvers sign off.</p>
              </div>
              <div className="approval-help-section">
                <h3>Approving a request</h3>
                <p>Approvers see pending requests in the <strong>For Approval</strong> tab. You can <strong>Approve</strong>, <strong>Reject</strong>, <strong>Return</strong> (ask for more info), or <strong>Wait</strong>. Decisions require a clear reason.</p>
              </div>
              <div className="approval-help-section">
                <h3>Signatures on the document</h3>
                <p>The generated PDF records the <strong>requester's signature</strong> (name, position, submission time) and each <strong>approver's signature</strong> (name, position, decision time). Every signer signs on the document.</p>
              </div>
              <div className="approval-help-section">
                <h3>Administrators</h3>
                <p>Use the <strong>Users</strong> tab to manage accounts (assigning a position makes that user an approver), the <strong>Categories</strong> tab to design each approval flow, and the <strong>Positions</strong> tab to manage sign-off roles.</p>
              </div>
              <div className="approval-actions">
                <button className="btn-approve" onClick={() => setHelpOpen(false)}>Got it</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const CategoryModal = ({ modal, setModal, positions, msg, onClose, onSave }) => {
  const updateStep = (index, role_title) => {
    setModal({ ...modal, workflow: modal.workflow.map((w, i) => (i === index ? { ...w, role_title } : w)) });
  };
  const addStep = () => {
    setModal({ ...modal, workflow: [...modal.workflow, { step_order: modal.workflow.length + 1, role_title: '' }] });
  };
  const removeStep = (index) => {
    setModal({ ...modal, workflow: modal.workflow.filter((_, i) => i !== index) });
  };
  const moveStep = (index, dir) => {
    const wf = [...modal.workflow];
    const j = index + dir;
    if (j < 0 || j >= wf.length) return;
    [wf[index], wf[j]] = [wf[j], wf[index]];
    setModal({ ...modal, workflow: wf });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{ maxWidth: 640 }} onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>×</button>
        <h2>{modal.mode === 'create' ? 'Add Category' : 'Edit Category'}</h2>
        <div className="modal-body">
          {msg && <div className="approval-msg error">{msg}</div>}
          <div className="approval-form-row">
            <label>Category name</label>
            <input type="text" value={modal.name} onChange={(e) => setModal({ ...modal, name: e.target.value })}
              placeholder="e.g. IT Equipment Request" />
          </div>
          <div className="approval-form-row">
            <label>Description</label>
            <input type="text" value={modal.description} onChange={(e) => setModal({ ...modal, description: e.target.value })}
              placeholder="Short description" />
          </div>
          <div className="approval-form-row">
            <label>Approval flow (first position signs first)</label>
            <div className="approval-wf-steps">
              {modal.workflow.length === 0 && (
                <p className="no-data">No steps yet. Add the positions that must sign this request, in order.</p>
              )}
              {modal.workflow.map((w, i) => (
                <div className="approval-wf-step" key={i}>
                  <span className="approval-wf-order">{i + 1}</span>
                  <select value={w.role_title} onChange={(e) => updateStep(i, e.target.value)}>
                    <option value="">Select position…</option>
                    {positions.filter((p) => p.isActive).map((p) => (
                      <option key={p.id} value={p.name}>{p.name}</option>
                    ))}
                  </select>
                  <button type="button" className="btn-view" onClick={() => moveStep(i, -1)} disabled={i === 0}>↑</button>
                  <button type="button" className="btn-view" onClick={() => moveStep(i, 1)} disabled={i === modal.workflow.length - 1}>↓</button>
                  <button type="button" className="btn-reject" onClick={() => removeStep(i)}>✕</button>
                </div>
              ))}
              <button type="button" className="btn-wait" onClick={addStep}>+ Add Step</button>
            </div>
          </div>
          <div className="approval-actions">
            <button className="btn-view" onClick={onClose}>Cancel</button>
            <button className="btn-approve" onClick={onSave}>
              {modal.mode === 'create' ? 'Create Category' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const EditUserModal = ({ user, setUser, positions, deptOptions, onClose, onSave }) => (
  <div className="modal-overlay" onClick={onClose}>
    <div className="modal-content" style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
      <button className="modal-close" onClick={onClose}>×</button>
      <h2>Edit User</h2>
      <div className="modal-body">
        <form className="approval-form" onSubmit={onSave}>
          <div className="approval-form-row">
            <label>Email</label>
            <input type="email" value={user.email || ''} onChange={(e) => setUser({ ...user, email: e.target.value })} required />
          </div>
          <div className="approval-form-row">
            <label>Full name</label>
            <input type="text" value={user.name || ''} onChange={(e) => setUser({ ...user, name: e.target.value })} />
          </div>
          <div className="approval-form-row">
            <label>Department</label>
            <select value={user.department || 'General'} onChange={(e) => setUser({ ...user, department: e.target.value })}>
              {deptOptions.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div className="approval-form-row">
            <label>Role</label>
            <select value={user.role} onChange={(e) => setUser({ ...user, role: e.target.value })}>
              <option value="requester">Requester</option>
              <option value="finance">Finance</option>
              <option value="admin">Administrator</option>
            </select>
          </div>
          <div className="approval-form-row">
            <label>Position (makes this user an approver for that position)</label>
            <select value={user.position || ''} onChange={(e) => setUser({ ...user, position: e.target.value })}>
              <option value="">No position</option>
              {positions.filter((p) => p.isActive).map((p) => (
                <option key={p.id} value={p.name}>{p.name}</option>
              ))}
            </select>
          </div>
          <div className="approval-actions">
            <button className="btn-view" type="button" onClick={onClose}>Cancel</button>
            <button className="btn-approve" type="submit">Save Changes</button>
          </div>
        </form>
      </div>
    </div>
  </div>
);

const EditNamedModal = ({ title, item, setItem, namePlaceholder, onClose, onSave }) => (
  <div className="modal-overlay" onClick={onClose}>
    <div className="modal-content" style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
      <button className="modal-close" onClick={onClose}>×</button>
      <h2>{title}</h2>
      <div className="modal-body">
        <form className="approval-form" onSubmit={(e) => { e.preventDefault(); onSave(); }}>
          <div className="approval-form-row">
            <label>Name</label>
            <input type="text" value={item.name || ''} onChange={(e) => setItem({ ...item, name: e.target.value })} placeholder={namePlaceholder} required />
          </div>
          <div className="approval-form-row">
            <label>Description (optional)</label>
            <input type="text" value={item.description || ''} onChange={(e) => setItem({ ...item, description: e.target.value })} />
          </div>
          <div className="approval-actions">
            <button className="btn-view" type="button" onClick={onClose}>Cancel</button>
            <button className="btn-approve" type="submit">Save Changes</button>
          </div>
        </form>
      </div>
    </div>
  </div>
);

const ActionModal = ({ request, modal, submitting, onClose, onConfirm }) => {
  const [reason, setReason] = React.useState('');
  const needsReason = ['reject', 'return', 'wait'].includes(modal.action);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>×</button>
        <h2>{modal.label}</h2>
        <div className="modal-body">
          <p style={{ fontSize: 14, color: '#334155' }}>
            <strong>{request?.requestNumber}</strong> — {request?.title}
          </p>
          {needsReason && (
            <div>
              <label style={{ fontWeight: 700, fontSize: 13 }}>Reason (required):</label>
              <textarea
                className="approval-reason-input"
                rows="4"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Provide a clear reason so the requester understands the decision."
              />
            </div>
          )}
          <div className="approval-actions">
            <button className="btn-view" onClick={onClose} disabled={submitting}>Cancel</button>
            <button
              className={modal.action === 'approve' ? 'btn-approve' : 'btn-reject'}
              disabled={submitting || (needsReason && !reason.trim())}
              onClick={() => onConfirm(modal.action, reason.trim())}
            >
              {submitting ? 'Processing…' : `Confirm ${modal.label}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApprovalDashboard;
